import { Form } from 'antd';
import { RevxRadio } from 'components';
import {
  JobconfigAddQuantityIcon,
  JobconfigDeleteIcon,
} from 'components/Icons';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxRadioProps } from 'components/RevxRadio';
import { FieldError } from 'rc-field-form/lib/interface';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigSvcProps } from '..';
import CommonJobSvcBorder from '../common/CommonJobSvcBorder';
import CommonJobSvcCotainer from '../common/CommonJobSvcCotainer';
import CommonJobSvcInstructions from '../common/CommonJobSvcInstructions';
import CommonJobSvcMessage from '../common/CommonJobSvcMessage';
import CommonJobSvcName from '../common/CommonJobSvcName';
import CommonQuantityPerBundle from '../common/CommonQuantityPerBundle';
import { useHideSvc } from '../hook/useHideSvc';

const Perforate = (props: ConfigSvcProps) => {
  const { id, jobOptionSvc, formCache, spec, form, isOneof, sku } = props;

  const hideSvc = useHideSvc(props);

  // const hideSvcContainer = useHideSvcContainer(props);

  const [formErrorTip, setFormErrorTip] = useState<FieldError[]>();

  const partSpec = useMemo(() => {
    if (!spec?.parts?.length) {
      return spec;
    } else {
      return spec?.parts?.find((part) => part.skuId === sku.id);
    }
  }, [spec, sku.id]);

  const optionList = useCallback(
    (label: string) => {
      return jobOptionSvc.specs.map((spec) => {
        return {
          value:
            spec.parameters.find((parameter) => parameter.label === label)
              ?.parameter || '',
          children: (
            <span>
              {
                spec.parameters.find((parameter) => parameter.label === label)
                  ?.parameter
              }
            </span>
          ),
        };
      });
    },
    [jobOptionSvc.specs]
  );

  const perforateRadioProps = (label: string): RevxRadioProps => {
    return {
      outSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        wrap: 'nowrap',
        className: 'jobconfig-jobOption-content-outSideOption-default',
        style: {
          paddingBottom: 0,
        },
      },
      inSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        className: 'jobconfig-jobOption-content-inSideOption-default',
        style: {
          marginRight: 29,
        },
      },
      options: optionList(label),
    };
  };

  const pageRadioProps = useCallback((idx): RevxRadioProps => {
    return {
      outSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        wrap: 'wrap',
        className: 'jobconfig-jobOption-content-outSideOption-default',
        style: {
          paddingBottom: 0,
          marginLeft: idx > 0 ? 73 : 0,
        },
      },
      inSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        className: 'jobconfig-jobOption-content-inSideOption-default',
        style: {
          // marginRight: 29,
        },
      },
      options: [
        {
          value: 'All',
          children: <span style={{ paddingRight: '29px' }}>All</span>,
        },
        {
          value: 'Range',
          children: <span>Range</span>,
        },
      ].filter((item) => !(item.value === 'All' && idx > 0)),
    };
  }, []);

  const checkForm = useCallback(async () => {
    try {
      await form.validateFields();
      setFormErrorTip([]);
    } catch {
      setFormErrorTip(form.getFieldsError());
    }
  }, [form]);

  useEffect(() => {
    if (!formCache?.[id] || !formCache?.[id]?.['perforate']) {
      form.resetFields();
      form.setFieldsValue(formCache);
    }
    if (formCache?.[id]?.['perforate']) {
      if (!formCache[id]['perforate'][0]) {
        formCache[id]['perforate'][0] = {};
      }
      if (formCache[id]?.['perforate'].length === 1) {
        if (formCache[id]?.['perforate'][0].page === 'Range') {
          formCache[id]['perforate'][0].rangeStart = 1;
          formCache[id]['perforate'][0].rangeTo = spec?.pageCount || 1;
        }
      } else {
        if (formCache[id]?.['perforate'][0].page === 'All') {
          formCache[id]['perforate'] = [formCache[id]?.['perforate'][0]];
        } else {
          formCache[id]['perforate'].forEach(
            (perforate: { [x: string]: string }, idx: number) => {
              jobOptionSvc.parameterLabels.forEach((label) => {
                const labelList = optionList(label).map((item) => item.value);
                if (!formCache[id]['perforate'][idx]) {
                  formCache[id]['perforate'][idx] = {};
                }
                if (!['page', 'rangeStart', 'rangeTo'].includes(label)) {
                  if (
                    !formCache[id]['perforate'][idx][label] ||
                    !labelList.includes(formCache[id]['perforate'][idx][label])
                  ) {
                    formCache[id]['perforate'][idx][label] = labelList[0];
                  }
                }
                if (!formCache[id]['perforate'][idx]['page']) {
                  formCache[id]['perforate'][idx]['page'] = 'Range';
                }
              });
            }
          );
        }
      }
      form.setFieldsValue(formCache);
    } else {
      if (formCache?.[id]?.['name']) {
        formCache[id]['perforate'] = [
          {
            page: 'All',
            rangeStart: 1,
            rangeTo: spec?.pageCount || 1,
          },
        ];
        jobOptionSvc.parameterLabels.forEach((label) => {
          const labelList = optionList(label).map((item) => item.value);
          formCache[id]['perforate'][0][label] = labelList[0];
        });
        form.setFieldsValue(formCache);
      }
    }
    checkForm();
  }, [
    formCache,
    id,
    spec,
    jobOptionSvc.parameterLabels,
    optionList,
    form,
    checkForm,
  ]);

  const errorTip = useMemo(() => {
    return formErrorTip?.find(
      (item) =>
        (item.name.includes('rangeTo') || item.name.includes('rangeStart')) &&
        item.errors.length
    )?.errors[0];
  }, [formErrorTip]);

  return (
    <>
      <div style={hideSvc}>
        <RevxFlexLayout align="top" justify="justify-start">
          {isOneof ? <></> : <CommonJobSvcName {...props} />}
          <div>
            <CommonJobSvcCotainer {...props}>
              {formCache?.[id]?.['name'] ? (
                <Form.List name={[id, 'perforate']}>
                  {(fields, { add, remove }, { errors }) =>
                    fields.map((field, idx) => (
                      <Fragment key={`${field.key}-perforate`}>
                        <RevxFlexLayout align="stretch">
                          <div
                            className={
                              partSpec?.pageCountEditable
                                ? `jobconfig-jobOption-perforate-border`
                                : ''
                            }
                          >
                            {jobOptionSvc.parameterLabels.map((label) => (
                              <Fragment key={`${label}-${idx}-perforate`}>
                                <div
                                  className="jobconfig-jobOption-content-label"
                                  hidden={idx !== 0}
                                  style={{ marginBottom: 10 }}
                                >
                                  {label}
                                </div>
                                <div>
                                  <Form.Item
                                    {...field}
                                    key={`${field.name}-${label}`}
                                    name={[field.name, label]}
                                    noStyle
                                  >
                                    <RevxRadio
                                      {...perforateRadioProps(label)}
                                    />
                                  </Form.Item>
                                </div>
                              </Fragment>
                            ))}
                          </div>
                          <div
                            style={{ paddingLeft: 28 }}
                            hidden={!partSpec?.pageCountEditable}
                          >
                            <div
                              className="jobconfig-jobOption-content-label"
                              style={{ marginBottom: 10 }}
                              hidden={idx !== 0}
                            >
                              pages
                            </div>
                            <RevxFlexLayout style={{ marginBottom: 10 }}>
                              <Form.Item
                                {...field}
                                key={`${field.name}-page`}
                                name={[field.name, 'page']}
                                noStyle
                              >
                                <RevxRadio {...pageRadioProps(idx)} />
                              </Form.Item>
                              <RevxFlexLayout
                                style={{ marginLeft: '29px' }}
                                hidden={
                                  formCache?.[id]?.['perforate']?.[
                                    field.fieldKey
                                  ]?.['page'] === 'All'
                                }
                              >
                                <Form.Item
                                  {...field}
                                  key={`${field.name}-rangeStart`}
                                  name={[field.name, 'rangeStart']}
                                  noStyle
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const perforate = getFieldValue([
                                          id,
                                          'perforate',
                                        ])[field.name];
                                        const previousPerforate = getFieldValue(
                                          [id, 'perforate']
                                        )[field.name - 1];
                                        if (
                                          !value &&
                                          perforate?.page !== 'All' &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        if (
                                          value > perforate?.rangeTo &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        if (
                                          value <= previousPerforate?.rangeTo &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <RevxInputNumber
                                    isInteger
                                    className="jobconfig-jobOption-perforate-input"
                                    style={{ marginLeft: -13 }}
                                  />
                                </Form.Item>
                                <span style={{ padding: '0 6px' }}>To</span>
                                <Form.Item
                                  {...field}
                                  key={`${field.name}-rangeTo`}
                                  name={[field.name, 'rangeTo']}
                                  noStyle
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const perforate = getFieldValue([
                                          id,
                                          'perforate',
                                        ])[field.name];
                                        const nextPerforate = getFieldValue([
                                          id,
                                          'perforate',
                                        ])[field.name + 1];
                                        if (
                                          !value &&
                                          perforate?.page !== 'All' &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        if (
                                          value < perforate?.rangeStart &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        if (
                                          value > (partSpec?.pageCount || 1) &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              `Perforate range can't exceed the page count`
                                            )
                                          );
                                        }
                                        if (
                                          nextPerforate &&
                                          value >= nextPerforate?.rangeStart &&
                                          partSpec?.skuId
                                        ) {
                                          return Promise.reject(
                                            new Error(
                                              'Please check the overlapping page range '
                                            )
                                          );
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <RevxInputNumber
                                    isInteger
                                    className="jobconfig-jobOption-perforate-input"
                                    style={{ marginRight: 6 }}
                                  />
                                </Form.Item>
                                <div
                                  onClick={() => add()}
                                  style={{ marginRight: 4 }}
                                >
                                  <JobconfigAddQuantityIcon />
                                </div>
                                <div
                                  hidden={field.fieldKey === 0}
                                  onClick={() => remove(field.name)}
                                  className="default-jobConfig-svg"
                                >
                                  <JobconfigDeleteIcon />
                                </div>
                              </RevxFlexLayout>
                            </RevxFlexLayout>
                          </div>
                        </RevxFlexLayout>
                      </Fragment>
                    ))
                  }
                </Form.List>
              ) : (
                <RevxFlexLayout align="stretch">
                  <div>
                    {jobOptionSvc.parameterLabels.map((label) => (
                      <Fragment key={`${label}-perforate`}>
                        <div
                          className="jobconfig-jobOption-content-label"
                          style={{ marginBottom: 10 }}
                        >
                          {label}
                        </div>
                        <div>
                          <Form.Item noStyle>
                            <RevxRadio
                              {...perforateRadioProps(label)}
                              disabled
                            />
                          </Form.Item>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </RevxFlexLayout>
              )}
              <CommonQuantityPerBundle {...props} />
              <CommonJobSvcInstructions {...props} />
              {errorTip ? (
                <span className="ant-form-item-explain-error">{errorTip}</span>
              ) : (
                <></>
              )}

              <div style={{ marginTop: 10 }}>
                <CommonJobSvcMessage {...props} />
              </div>
            </CommonJobSvcCotainer>
          </div>
        </RevxFlexLayout>
        <div
        // className={
        //   isOneof ? 'base-service-container-grid' : 'base-service-container'
        // }
        // hidden={hideSvcContainer}
        ></div>
        <CommonJobSvcBorder />
      </div>
    </>
  );
};

export default Perforate;
